import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { cn, downloadJSON } from '@/lib/utils'
import { lcaAPI } from '@/lib/api/client'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Loader2,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  X,
  Filter,
  ChevronDown,
  ChevronUp,
  GripVertical,
  ExternalLink,
  Download,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Play,
  Calculator,
} from 'lucide-react'
import type { ActivityWithExchanges, ForegroundTree } from '@/lib/types/project-graph'
import type { TaskPendingError } from '@/lib/types'
import { extractHotspotErrorMessage } from './hotspotErrors'
import { AttributionSankey } from './AttributionSankey'

const DEFAULT_METHOD_BY_DB: Record<string, string> = {
  'ecoinvent-3.6-cutoff':
    'ecoinvent-3.6 > IPCC 2013 > climate change > GWP 100a',
  'ecoinvent-3.7.1-cutoff':
    'ecoinvent-3.7.1 > IPCC 2013 > climate change > GWP 100a',
  'ecoinvent-3.8-cutoff':
    'ecoinvent-3.8 > IPCC 2013 > climate change > GWP 100a',
  'ecoinvent-3.9.1-cutoff':
    'ecoinvent-3.9.1 > IPCC 2021 > climate change > global warming potential (GWP100)',
  'ecoinvent-3.10.1-cutoff':
    'ecoinvent-3.10.1 > IPCC 2021 > climate change > global warming potential (GWP100)',
  'ecoinvent-3.11-cutoff':
    'ecoinvent-3.11 > IPCC 2021 > climate change: total (excl. biogenic CO2) > global warming potential (GWP100)',
  'ecoinvent-3.12-cutoff':
    'ecoinvent-3.12 > IPCC 2021 > climate change: total (excl. biogenic CO2) > global warming potential (GWP100)',
}

function shortMethodLabel(spec: string): string {
  const segs = spec.split(' > ').filter(Boolean)
  if (segs.length <= 2) return spec
  return segs.slice(-2).join(' · ')
}

interface SankeyViewProps {
  activities: ActivityWithExchanges[]
  projectName?: string
  projectDatabase?: string
  selectedNodeId?: string | null
  onNodeClick?: (nodeId: string, nodeType: 'activity' | 'exchange') => void
}

interface MethodInfo {
  method_specification: string
  category: string
  subcategory: string
  indicator: string
  unit: string
}

interface HotspotEntry {
  rank: number
  activity_name: string
  reference_product: string
  activity_code: string
  location: string
  database: string
  absolute_score: number
  contribution_pct: number
  unit: string
  classification?: string
  isic_category?: string
  ecoquery_url?: string
  attributed_to?: Record<string, number>
  foreground_metadata?: {
    life_cycle_stage?: string | null
    hierarchy_level?: number | null
    supply_chain_tier?: string | null
    [key: string]: unknown
  }
}

interface ForegroundBiosphereExchange {
  fg_code: string
  bio_activity_code: string
  name: string
  amount: number
  unit: string
  compartment: string
}

interface HotspotResult {
  lca_score: { value: number; unit: string }
  hotspots: HotspotEntry[]
  method_specification?: string
  foreground_tree?: ForegroundTree
  foreground_biosphere_exchanges?: ForegroundBiosphereExchange[]
}

type CalcStatus = 'idle' | 'searching' | 'ready' | 'submitting' | 'polling' | 'done' | 'error'

const POPULAR_CATEGORIES = [
  'IPCC 2021',
  'ReCiPe 2016 v1.03, midpoint (H)',
  'EF v3.1',
  'CML v4.8 2016',
  'TRACI v2.1',
]

function pctColor(pct: number): string {
  if (pct >= 40) return '#ef4444'
  if (pct >= 20) return '#f97316'
  if (pct >= 10) return '#f59e0b'
  if (pct >= 5) return '#eab308'
  return '#22c55e'
}

function fmtVal(v: number): string {
  if (v === 0) return '0'
  if (Math.abs(v) < 0.001) return v.toExponential(3)
  if (Math.abs(v) < 1) return v.toFixed(4)
  if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 2 })
  return v.toFixed(3)
}

export function SankeyView({
  activities,
  projectName,
  projectDatabase,
}: SankeyViewProps) {
  // The hotspot endpoint only accepts the project's root (functional unit); a
  // The hotspot endpoint only accepts the project's root (functional unit); a
  // non-root target is rejected HTTP 422. Target the activity the API flags as
  // the root — authoritatively, not guessed. If the project has no single root
  // we surface that (below) rather than auto-picking a wrong activity.
  const selectedActivity = useMemo(
    () => activities.find((a) => a.is_root === true) ?? null,
    [activities],
  )
  // Project has activities but none is a root — a malformed model with no clear
  // functional unit. Block the run and tell the user instead of guessing.
  const noFunctionalUnit = activities.length > 0 && selectedActivity === null

  const [status, setStatus] = useState<CalcStatus>('idle')
  const [methodQuery, setMethodQuery] = useState('')
  const [methods, setMethods] = useState<MethodInfo[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedMethod, setSelectedMethod] = useState<MethodInfo | null>(null)
  const [methodsOpen, setMethodsOpen] = useState(false)
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const [result, setResult] = useState<HotspotResult | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [topN, setTopN] = useState(20)
  const [selectedHotspot, setSelectedHotspot] = useState<HotspotEntry | null>(null)
  const [sankeyZoom, setSankeyZoom] = useState(1)
  const [toolbarCollapsed, setToolbarCollapsed] = useState(true)
  const [toolbarOffsetX, setToolbarOffsetX] = useState(0)
  const toolbarDragRef = useRef<{ startX: number; startOffset: number } | null>(null)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onToolbarDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      toolbarDragRef.current = { startX: e.clientX, startOffset: toolbarOffsetX }
      const onMove = (ev: MouseEvent) => {
        if (!toolbarDragRef.current) return
        setToolbarOffsetX(toolbarDragRef.current.startOffset + ev.clientX - toolbarDragRef.current.startX)
      }
      const onUp = () => {
        toolbarDragRef.current = null
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [toolbarOffsetX],
  )

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [])

  useEffect(() => {
    if (!projectDatabase) return
    lcaAPI
      .getMethodCategories(projectDatabase)
      .then((res) => setCategories(res.data?.categories || []))
      .catch(() => {})
  }, [projectDatabase])

  const projectKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const nextKey = `${projectName ?? ''}::${projectDatabase ?? ''}`
    if (projectKeyRef.current !== null && projectKeyRef.current !== nextKey) {
      if (pollRef.current) clearTimeout(pollRef.current)
      setResult(null)
      setSelectedMethod(null)
      setSelectedCategory(null)
      setMethodQuery('')
      setMethods([])
      setStatus('idle')
      setErrorMsg(null)
      setSelectedHotspot(null)
      setToolbarCollapsed(true)
      setToolbarOffsetX(0)
      didPrefillRef.current = false
    }
    projectKeyRef.current = nextKey
  }, [projectName, projectDatabase])

  const didPrefillRef = useRef(false)

  const doSearch = useCallback(
    async (kw: string, cat?: string | null) => {
      if (!kw.trim() || !projectDatabase) return
      setStatus('searching')
      try {
        const params: { keywords: string; database: string; category?: string; limit: number } = {
          keywords: kw.trim(),
          database: projectDatabase,
          limit: 30,
        }
        if (cat) params.category = cat
        const res = await lcaAPI.searchMethods(params)
        setMethods(res.data?.methods || [])
        setMethodsOpen(true)
        setStatus('idle')
      } catch {
        toast.error('Method search failed')
        setStatus('idle')
      }
    },
    [projectDatabase],
  )

  useEffect(() => {
    if (didPrefillRef.current) return
    if (!projectDatabase) return
    if (selectedMethod || methodQuery.trim() || selectedCategory) return
    const canonical = DEFAULT_METHOD_BY_DB[projectDatabase]
    if (!canonical) return
    const segs = canonical.split(' > ').filter(Boolean)
    didPrefillRef.current = true
    setSelectedMethod({
      method_specification: canonical,
      category: segs[1] ?? '',
      subcategory: segs[2] ?? '',
      indicator: segs[3] ?? segs[segs.length - 1] ?? '',
      unit: 'kg CO2-Eq',
    })
  }, [projectDatabase, selectedMethod, methodQuery, selectedCategory])

  const handleSelectCategory = (cat: string) => {
    const next = selectedCategory === cat ? null : cat
    setSelectedCategory(next)
    const kw = methodQuery.trim() || cat
    if (next) {
      setMethodQuery(kw === cat ? cat : kw)
      doSearch(kw, next)
    } else if (methodQuery.trim()) {
      doSearch(methodQuery, null)
    }
  }

  const handleSelectMethod = (m: MethodInfo) => {
    setSelectedMethod(m)
    setMethodsOpen(false)
    setStatus('ready')
    setResult(null)
    setErrorMsg(null)
  }

  const pollForResult = useCallback(async (taskId: string, attempt = 0) => {
    if (attempt > 60) {
      setStatus('error')
      setErrorMsg('Calculation timed out')
      return
    }
    try {
      const res = await lcaAPI.getTaskStatus(taskId)
      if (['PENDING', 'STARTED', 'PROGRESS'].includes(res.data.status)) {
        pollRef.current = setTimeout(() => pollForResult(taskId, attempt + 1), 2000)
        return
      }
      if (res.data.status === 'FAILURE') {
        setStatus('error')
        setErrorMsg(res.data.error || 'Calculation failed')
        return
      }
      if (res.data.status === 'SUCCESS' && res.data.result?.hotspots) {
        setResult(res.data.result as HotspotResult)
        setStatus('done')
        toast.success('Hotspot analysis complete')
      }
    } catch (err) {
      const detail = (err as TaskPendingError).response?.data?.detail
      if (
        detail?.details?.status === 'PENDING' ||
        detail?.details?.status === 'STARTED' ||
        detail?.details?.status === 'PROGRESS'
      ) {
        pollRef.current = setTimeout(() => pollForResult(taskId, attempt + 1), 2000)
        return
      }
      setStatus('error')
      setErrorMsg(extractHotspotErrorMessage(err))
    }
  }, [])

  const handleExport = useCallback(() => {
    if (!result) return
    const ts = new Date().toISOString().replace(/[:.]/g, '-')
    const safeProject = (projectName || 'project').replace(/[^a-z0-9_-]+/gi, '_')
    downloadJSON(result, `hotspot-${safeProject}-${ts}.json`)
  }, [result, projectName])

  const handleCalculate = async () => {
    if (!selectedMethod || !selectedActivity || !projectName) return
    setStatus('submitting')
    setErrorMsg(null)
    setResult(null)
    try {
      const res = await lcaAPI.calculateHotspotsCustom({
        project_name: projectName,
        activity_code: selectedActivity.code,
        method_specification: selectedMethod.method_specification,
        top_n: topN,
      })
      const taskId = res.data.task_id
      if (!taskId) {
        setStatus('error')
        setErrorMsg('No task ID returned')
        return
      }
      setStatus('polling')
      setToolbarCollapsed(true)
      pollRef.current = setTimeout(() => pollForResult(taskId, 0), 4000)
    } catch (err) {
      setStatus('error')
      setErrorMsg(extractHotspotErrorMessage(err, 'Failed to start'))
    }
  }

  const isWorking = status === 'submitting' || status === 'polling'

  if (activities.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-center text-muted-foreground p-8">
        <p className="text-sm">Select a project to run impact analysis.</p>
      </div>
    )
  }

  if (!projectName || !projectDatabase) {
    return (
      <div className="flex items-center justify-center h-full text-center text-muted-foreground p-8">
        <p className="text-sm">Project context not available.</p>
      </div>
    )
  }

  const runDisabled = !selectedMethod || !selectedActivity || isWorking

  return (
    <>
      <div className="relative h-full w-full overflow-auto">
        {!result && !isWorking && (
          <div className="flex items-center justify-center h-full text-center text-muted-foreground p-8">
            <div>
              <Calculator className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">Impact Flow Diagram</p>
              <p className="text-xs mt-1">
                Select a method and run the analysis to see upstream contributions.
              </p>
            </div>
          </div>
        )}

        {isWorking && (
          <div className="flex items-center justify-center h-full text-muted-foreground gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Running hotspot analysis…</span>
          </div>
        )}

        {result &&
          result.foreground_tree &&
          result.hotspots?.some(
            (h) => h.attributed_to && Object.keys(h.attributed_to).length > 0,
          ) && (
            <div
              style={{
                width: `${sankeyZoom * 100}%`,
                height: `${sankeyZoom * 100}%`,
                minWidth: '100%',
                minHeight: '100%',
              }}
            >
              <AttributionSankey
                hotspots={result.hotspots}
                foregroundTree={result.foreground_tree}
                totalScore={result.lca_score.value}
                totalUnit={result.lca_score.unit}
                methodName={selectedMethod?.indicator}
                projectName={projectName}
                projectDatabase={projectDatabase}
                activities={activities}
                foregroundBiosphereExchanges={result.foreground_biosphere_exchanges}
              />
            </div>
          )}

        {result &&
          (!result.foreground_tree ||
            !result.hotspots?.some(
              (h) => h.attributed_to && Object.keys(h.attributed_to).length > 0,
            )) && (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3 px-8 text-center">
              <div className="text-sm font-medium">
                Attribution data unavailable for this result
              </div>
              <div className="text-xs max-w-md">
                {!result.foreground_tree
                  ? 'The hotspot result is missing the foreground tree. Re-run the hotspot analysis to refresh.'
                  : 'No hotspots have attribution data. This usually means the result was computed before the latest worker fix — re-run the hotspot to refresh.'}
              </div>
              <button
                className="px-3 py-1.5 text-xs rounded border border-border hover:bg-muted"
                onClick={handleCalculate}
                disabled={status === 'submitting' || status === 'polling'}
              >
                Re-run hotspot
              </button>
            </div>
          )}

        <div
          className="absolute top-2 left-1/2 z-30 bg-background/95 backdrop-blur border border-border rounded-md shadow-md max-w-[min(720px,calc(100%-1rem))]"
          style={{ transform: `translateX(calc(-50% + ${toolbarOffsetX}px))` }}
        >
          {toolbarCollapsed ? (
            <div className="flex items-center gap-2 pl-1 pr-3 py-1.5 text-xs">
              <button
                type="button"
                onMouseDown={onToolbarDragStart}
                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0"
                title="Drag toolbar"
              >
                <GripVertical className="h-3.5 w-3.5" />
              </button>
              {selectedMethod ? (
                <span
                  className="font-medium"
                  title={selectedMethod.method_specification}
                >
                  {shortMethodLabel(selectedMethod.method_specification)}
                </span>
              ) : (
                <span className="italic text-muted-foreground">No method selected</span>
              )}
              <span className="text-muted-foreground">
                Top: <span className="font-mono text-foreground">{topN}</span>
              </span>
              <Button
                size="sm"
                className="h-7 gap-1.5"
                onClick={handleCalculate}
                disabled={runDisabled}
              >
                {isWorking ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Play className="h-3 w-3" />
                )}
                Run
              </Button>
              <button
                type="button"
                onClick={() => setToolbarCollapsed(false)}
                className="h-6 w-6 flex items-center justify-center rounded hover:bg-accent text-muted-foreground"
                title="Expand toolbar"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="pl-1 pr-3 py-2 space-y-2 w-[min(720px,calc(100vw-2rem))]">
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  onMouseDown={onToolbarDragStart}
                  className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
                  title="Drag toolbar"
                >
                  <GripVertical className="h-3.5 w-3.5" />
                </button>
                <span className="text-xs text-muted-foreground shrink-0 mt-0.5">Method:</span>
                <div className="flex-1 min-w-0 text-xs">
                  {selectedMethod ? (
                    <span className="break-words">{selectedMethod.method_specification}</span>
                  ) : (
                    <span className="italic text-muted-foreground">Select an impact method…</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setMethodsOpen((o) => !o)}
                  className="text-xs text-primary hover:underline shrink-0"
                >
                  [change]
                </button>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-muted-foreground">
                  FU: <span className="text-foreground">{selectedActivity?.name ?? '—'}</span>
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Top:</span>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={topN}
                    onChange={(e) => {
                      const v = Number(e.target.value)
                      if (!Number.isNaN(v)) setTopN(Math.max(1, Math.min(100, v)))
                    }}
                    disabled={isWorking}
                    className="h-7 w-16 text-xs"
                  />
                </div>
                <Button
                  size="sm"
                  className="h-7 gap-1.5"
                  onClick={handleCalculate}
                  disabled={runDisabled}
                >
                  {isWorking ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Play className="h-3 w-3" />
                  )}
                  Run
                </Button>
                <button
                  type="button"
                  onClick={() => setToolbarCollapsed(true)}
                  className="ml-auto h-6 w-6 flex items-center justify-center rounded hover:bg-accent text-muted-foreground"
                  title="Collapse toolbar"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
              </div>
              {status === 'polling' && (
                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Waiting for worker…
                </p>
              )}
            </div>
          )}
          {/* Errors surface in BOTH collapsed and expanded states — a failed run
              from the default (collapsed) toolbar must not be silent. */}
          {status === 'error' && errorMsg && (
            <div className="mx-2 mb-2 p-2 rounded border border-destructive/50 bg-destructive/5 flex items-start gap-1.5">
              <XCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
              <p className="text-[10px] text-destructive">{errorMsg}</p>
            </div>
          )}
          {noFunctionalUnit && (
            <div className="mx-2 mb-2 p-2 rounded border border-amber-500/50 bg-amber-500/5 flex items-start gap-1.5">
              <XCircle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-700 dark:text-amber-500">
                This project has no functional unit — no activity is a root (every one is consumed
                by another). Define a single top-level activity before running a hotspot analysis.
              </p>
            </div>
          )}
        </div>

        {methodsOpen && !toolbarCollapsed && (
          <div className="absolute top-32 left-1/2 -translate-x-1/2 z-30 bg-background/95 backdrop-blur border border-border rounded-md shadow-md w-[min(720px,calc(100%-1rem))] max-h-[60%] overflow-y-auto">
            <div className="p-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Impact Method</p>

              <div className="flex flex-wrap gap-1">
                {POPULAR_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => handleSelectCategory(cat)}
                    disabled={isWorking}
                    className={cn(
                      'px-2 py-0.5 rounded-full text-[10px] border transition-colors',
                      selectedCategory === cat
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted/50 border-border hover:bg-accent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {cat.replace(/^ReCiPe 2016 v1\.03, /, 'ReCiPe ')}
                  </button>
                ))}
                <button
                  onClick={() => setCategoriesOpen(!categoriesOpen)}
                  disabled={isWorking}
                  className="px-2 py-0.5 rounded-full text-[10px] border border-border hover:bg-accent text-muted-foreground flex items-center gap-0.5"
                >
                  <Filter className="h-2.5 w-2.5" />
                  All
                </button>
              </div>

              {categoriesOpen && categories.length > 0 && (
                <div className="max-h-32 overflow-y-auto border border-border rounded p-1.5 space-y-0.5">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => {
                        handleSelectCategory(cat)
                        setCategoriesOpen(false)
                      }}
                      className={cn(
                        'w-full text-left px-2 py-0.5 rounded text-[10px] transition-colors',
                        selectedCategory === cat
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'hover:bg-accent text-muted-foreground',
                      )}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}

              {selectedCategory && (
                <div className="flex items-center gap-1 text-[10px]">
                  <span className="text-muted-foreground">Filter:</span>
                  <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                    {selectedCategory}
                    <button onClick={() => setSelectedCategory(null)}>
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                </div>
              )}

              <div className="flex gap-1.5">
                <Input
                  placeholder="e.g. climate, water..."
                  value={methodQuery}
                  onChange={(e) => setMethodQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && doSearch(methodQuery, selectedCategory)}
                  className="h-7 text-xs"
                  disabled={isWorking}
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => doSearch(methodQuery, selectedCategory)}
                  disabled={!methodQuery.trim() || status === 'searching' || isWorking}
                >
                  {status === 'searching' ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Search className="h-3 w-3" />
                  )}
                </Button>
              </div>

              {selectedMethod && (
                <div className="p-2 rounded-md border border-primary bg-primary/5 flex items-start gap-2">
                  <CheckCircle className="h-3 w-3 text-primary shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{selectedMethod.indicator}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {selectedMethod.category} — {selectedMethod.unit}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedMethod(null)
                      setResult(null)
                      setStatus('idle')
                    }}
                    className="text-muted-foreground hover:text-foreground shrink-0"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}

              {methods.length > 0 && (
                <div>
                  <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    {methods.length} method{methods.length !== 1 ? 's' : ''}
                  </p>
                  <div className="mt-1 space-y-1 max-h-48 overflow-y-auto">
                    {methods.map((m) => (
                      <button
                        key={m.method_specification}
                        onClick={() => handleSelectMethod(m)}
                        className={cn(
                          'w-full text-left p-1.5 rounded border text-xs transition-colors',
                          selectedMethod?.method_specification === m.method_specification
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-accent/50',
                        )}
                      >
                        <span className="font-medium">{m.indicator}</span>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {m.category} &gt; {m.subcategory} — {m.unit}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {result && (
          <div className="absolute top-2 right-2 z-30 flex items-center gap-0.5 rounded-md border border-border bg-background/90 backdrop-blur p-0.5 shadow-sm">
            <button
              type="button"
              onClick={() => setSankeyZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
              disabled={sankeyZoom <= 0.5}
              className="h-6 w-6 flex items-center justify-center rounded hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
              title="Zoom out"
            >
              <ZoomOut className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => setSankeyZoom(1)}
              className="h-6 px-1.5 flex items-center justify-center rounded hover:bg-accent text-[10px] font-mono text-muted-foreground"
              title="Reset zoom"
            >
              {Math.round(sankeyZoom * 100)}%
            </button>
            <button
              type="button"
              onClick={() => setSankeyZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))}
              disabled={sankeyZoom >= 4}
              className="h-6 w-6 flex items-center justify-center rounded hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
              title="Zoom in"
            >
              <ZoomIn className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => setSankeyZoom(1)}
              className="h-6 w-6 flex items-center justify-center rounded hover:bg-accent"
              title="Fit"
            >
              <Maximize2 className="h-3 w-3" />
            </button>
          </div>
        )}

        {result && (
          <Button
            variant="outline"
            size="sm"
            className="absolute bottom-2 right-2 z-30 gap-1.5 text-xs h-7 bg-background/90 backdrop-blur"
            onClick={handleExport}
            title="Download hotspot result as JSON"
          >
            <Download className="h-3 w-3" />
            Download JSON
          </Button>
        )}

      </div>

      <Dialog
        open={!!selectedHotspot}
        onOpenChange={(open) => {
          if (!open) setSelectedHotspot(null)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm leading-snug pr-6">
              {selectedHotspot?.activity_name}
            </DialogTitle>
          </DialogHeader>
          {selectedHotspot && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2.5 rounded-md bg-muted/50 border border-border">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">
                    Contribution
                  </p>
                  <p
                    className="text-base font-bold font-mono"
                    style={{ color: pctColor(selectedHotspot.contribution_pct) }}
                  >
                    {selectedHotspot.contribution_pct.toFixed(2)}%
                  </p>
                </div>
                <div className="p-2.5 rounded-md bg-muted/50 border border-border">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">
                    Impact score
                  </p>
                  <p className="text-base font-bold font-mono">
                    {fmtVal(selectedHotspot.absolute_score)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{selectedHotspot.unit}</p>
                </div>
              </div>

              <div className="space-y-1.5">
                {[
                  ['Rank', `#${selectedHotspot.rank}`],
                  ['Reference product', selectedHotspot.reference_product],
                  ['Location', selectedHotspot.location],
                  ['Database', selectedHotspot.database],
                  ['Activity code', selectedHotspot.activity_code],
                  ['Classification', selectedHotspot.classification || '—'],
                  ...(selectedHotspot.foreground_metadata?.life_cycle_stage
                    ? [
                        [
                          'Life cycle stage',
                          selectedHotspot.foreground_metadata.life_cycle_stage as string,
                        ],
                      ]
                    : []),
                  ...(selectedHotspot.foreground_metadata?.hierarchy_level != null
                    ? [
                        [
                          'Hierarchy level',
                          String(selectedHotspot.foreground_metadata.hierarchy_level),
                        ],
                      ]
                    : []),
                  ...(selectedHotspot.foreground_metadata?.supply_chain_tier
                    ? [
                        [
                          'Supply chain tier',
                          selectedHotspot.foreground_metadata.supply_chain_tier as string,
                        ],
                      ]
                    : []),
                ].map(([label, value]) => (
                  <div key={label} className="flex gap-2">
                    <span className="text-muted-foreground w-32 shrink-0">{label}</span>
                    <span className="text-foreground break-all">{value}</span>
                  </div>
                ))}
              </div>

              {selectedHotspot.ecoquery_url && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2 mt-1"
                  onClick={() =>
                    window.open(selectedHotspot!.ecoquery_url!, '_blank', 'noopener,noreferrer')
                  }
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open in ecoinvent documentation
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
